import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BACKEND_URL } from '../config';
import fetchWithAuth from '../utils/fetchWithAuth';
import imageCompression from 'browser-image-compression';

const initialState = {
  vehiclePlate: '',
  date: '',
  driverLicense: '',
  origin: '',
  destination: '',
  startKm: '',
  endKm: '',
  gasGallons: '',
  cost: '',
  purpose: '',
  photos: [] // This will be an array of fileName strings for POST/PUT
};

const TripForm = ({ onSave, editingTrip, vehicles, drivers, onCancelEdit, assignments, refreshVehicles }) => {
  const [formData, setFormData] = useState(initialState);
  const [errorMsg, setErrorMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [photoPreviews, setPhotoPreviews] = useState([]); // Array of preview URLs
  const fileInputRef = useRef(null);

  // Get only active assignments (endDate null or future)
  const activeAssignments = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return assignments.filter(a =>
      (!a.endDate || a.endDate > today)
    );
  }, [assignments]);

  // Get unique vehicle-driver pairs from assignments
  const validPairs = useMemo(() => {
    return activeAssignments.map(a => ({
      vehiclePlate: a.vehiclePlate,
      driverLicense: a.driverLicense
    }));
  }, [activeAssignments]);

  // Get unique vehicles and drivers from valid pairs
  const validVehicles = useMemo(() => {
    return [...new Set(validPairs.map(p => p.vehiclePlate))];
  }, [validPairs]);
  const validDrivers = useMemo(() => {
    return [...new Set(validPairs.map(p => p.driverLicense))];
  }, [validPairs]);

  // When vehicle changes, filter drivers to only those with assignment for that vehicle
  const filteredDrivers = useMemo(() => {
    if (!formData.vehiclePlate) return [];
    return validPairs
      .filter(p => p.vehiclePlate === formData.vehiclePlate)
      .map(p => p.driverLicense);
  }, [formData.vehiclePlate, validPairs]);

  // When driver changes, filter vehicles to only those with assignment for that driver
  const filteredVehicles = useMemo(() => {
    if (!formData.driverLicense) return [];
    return validPairs
      .filter(p => p.driverLicense === formData.driverLicense)
      .map(p => p.vehiclePlate);
  }, [formData.driverLicense, validPairs]);

  // Get selected vehicle details
  const selectedVehicle = useMemo(
    () => vehicles.find(v => v.plate === formData.vehiclePlate),
    [vehicles, formData.vehiclePlate]
  );

  // Set startKm to vehicle currentMileage when vehicle changes (and not editing)
  useEffect(() => {
    if (formData.vehiclePlate && selectedVehicle && !editingTrip) {
      setFormData(prev => ({
        ...prev,
        startKm: selectedVehicle.currentMileage || 0
      }));
    }
    // If vehicle is cleared, also clear startKm
    if (!formData.vehiclePlate && !editingTrip) {
      setFormData(prev => ({
        ...prev,
        startKm: ''
      }));
    }
    // eslint-disable-next-line
  }, [formData.vehiclePlate, selectedVehicle, editingTrip]);

  // Keep driver/vehicle in sync with valid pairs
  useEffect(() => {
    if (formData.vehiclePlate && !filteredDrivers.includes(formData.driverLicense)) {
      setFormData(f => ({ ...f, driverLicense: '' }));
    }
    if (formData.driverLicense && !filteredVehicles.includes(formData.vehiclePlate)) {
      setFormData(f => ({ ...f, vehiclePlate: '' }));
    }
    // eslint-disable-next-line
  }, [formData.vehiclePlate, formData.driverLicense, filteredDrivers, filteredVehicles]);

  // When editing, set both fileName array and preview URLs
  useEffect(() => {
    const fetchAndSetPreview = async (fileName) => {
      try {
        const res = await fetchWithAuth(`${BACKEND_URL}/photos/get-signed-url?fileName=${encodeURIComponent(fileName)}`);
        const result = await res.json();
        if (result.file && result.file.url) {
          setPhotoPreviews([result.file.url]);
        } else {
          setPhotoPreviews([]); // Clear preview if URL fetch fails
        }
      } catch (err) {
        console.error("Error fetching signed URL for preview:", err);
        setPhotoPreviews([]);
      }
    };

    if (editingTrip) {
      const { id, photos = [], ...rest } = editingTrip;
      const photoFileNames = Array.isArray(photos) ? photos.map(p => typeof p === 'string' ? p : p.fileName) : [];
      setFormData({ ...rest, photos: photoFileNames });
      if (photoFileNames.length > 0) {
        fetchAndSetPreview(photoFileNames[0]);
      } else {
        setPhotoPreviews([]);
      }
    } else {
      setFormData(initialState);
      setPhotoPreviews([]);
    }
    setErrorMsg("");
  }, [editingTrip]);

  // Handle image upload (only 1 image, keep as array)
  const handlePhotoUpload = async (event) => {
    const imageFile = event.target.files[0];
    if (!imageFile) return;

    setUploading(true);
    setErrorMsg("");

    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920, // A good standard for quality vs size
        useWebWorker: true,
      };

      const compressedFile = await imageCompression(imageFile, options);

      const data = new FormData();
      // Important: pass the filename for the server
      data.append('files', compressedFile, compressedFile.name);
      data.append('kind', 'trip');
      data.append('vehiclePlate', formData.vehiclePlate);

      const res = await fetchWithAuth(`${BACKEND_URL}/photos/upload`, {
        method: 'POST',
        body: data,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Error al subir la imagen.");
      }

      const result = await res.json();
      // result.urls is an array of { url, fileName }
      if (result.urls && Array.isArray(result.urls) && result.urls.length > 0) {
        setFormData(prev => ({
          ...prev,
          photos: [result.urls[0].fileName], // Only keep the fileName for DB
        }));
        setPhotoPreviews([result.urls[0].url]);
      }
    } catch (err) {
      setErrorMsg(err.message || "Error al procesar o subir la imagen.");
      console.error(err);
    } finally {
      setUploading(false);
      // Reset input so user can re-upload the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemovePhoto = (idx) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== idx)
    }));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== idx));
    // Reset input so user can re-upload the same file
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate pair exists in assignments
    const pairExists = validPairs.some(
      p => p.vehiclePlate === formData.vehiclePlate && p.driverLicense === formData.driverLicense
    );
    if (!formData.vehiclePlate || !formData.driverLicense || !formData.date) {
      setErrorMsg("Vehículo, conductor y fecha son requeridos");
      return;
    }
    if (!pairExists) {
      setErrorMsg("Debe existir una asignación activa para el vehículo y conductor seleccionados.");
      return;
    }
    // Remove required validation for gasGallons and cost
    if (formData.gasGallons !== "" && (isNaN(Number(formData.gasGallons)) || Number(formData.gasGallons) < 0)) {
      setErrorMsg("Si ingresa combustible usado, debe ser un número positivo.");
      return;
    }
    if (formData.cost !== "" && (isNaN(Number(formData.cost)) || Number(formData.cost) < 0)) {
      setErrorMsg("Si ingresa el costo, debe ser un número positivo.");
      return;
    }
    const result = await onSave({
      ...formData,
      ...(editingTrip ? { id: editingTrip.id } : {})
    });
    if (result && result.error) {
      setErrorMsg(result.error);
      return;
    }
    setFormData(initialState);
    setPhotoPreviews([]);
    setErrorMsg("");
    if (editingTrip && onCancelEdit) onCancelEdit();
    // Refresh vehicles after save/update
    if (typeof refreshVehicles === "function") {
      refreshVehicles();
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-bold mb-4">{editingTrip ? "Editar Viaje" : "Registrar Viaje"}</h3>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Vehículo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehículo</label>
            <select
              name="vehiclePlate"
              value={formData.vehiclePlate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Seleccionar vehículo</option>
              {vehicles
                .filter(v => validVehicles.includes(v.plate) || v.plate === formData.vehiclePlate)
                .map(vehicle => (
                  <option key={vehicle.plate} value={vehicle.plate}>
                    {vehicle.brand} {vehicle.model} ({vehicle.plate})
                  </option>
                ))}
            </select>
            {selectedVehicle && (
              <div className="mt-2 text-xs text-gray-500">
                <div>Color: {selectedVehicle.color}</div>
                <div>Año: {selectedVehicle.year}</div>
                <div>Kilometraje actual: {selectedVehicle.currentMileage?.toLocaleString('es-MX') || 0} km</div>
                <div>Estado: {selectedVehicle.status}</div>
              </div>
            )}
          </div>
          {/* Conductor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Conductor</label>
            <select
              name="driverLicense"
              value={formData.driverLicense}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={!formData.vehiclePlate}
            >
              <option value="">Seleccionar conductor</option>
              {drivers
                .filter(driver =>
                  (formData.vehiclePlate
                    ? filteredDrivers.includes(driver.license)
                    : validDrivers.includes(driver.license)
                  ) || driver.license === formData.driverLicense
                )
                .map(driver => (
                  <option key={driver.license} value={driver.license}>
                    {driver.name} ({driver.license})
                  </option>
                ))}
            </select>
          </div>
          {/* Origen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Origen</label>
            <input
              type="text"
              name="origin"
              value={formData.origin}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          {/* Destino */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destino</label>
            <input
              type="text"
              name="destination"
              value={formData.destination}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          {/* Km inicio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Km inicio</label>
            <input
              type="number"
              name="startKm"
              value={formData.startKm}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
            />
          </div>
          {/* Km fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Km fin</label>
            <input
              type="number"
              name="endKm"
              value={formData.endKm}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          {/* Combustible Usado (gasGallons) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Combustible Usado (galones)</label>
            <input
              type="number"
              name="gasGallons"
              value={formData.gasGallons}
              onChange={handleChange}
              min="0"
              step="any"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          {/* Costo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Costo (S/)</label>
            <input
              type="number"
              name="cost"
              value={formData.cost}
              onChange={handleChange}
              min="0"
              step="any"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          {/* Fecha */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          {/* Motivo */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
            <input
              type="text"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          {/* Photos upload */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Foto del viaje</label>
            <div className="flex gap-4 flex-wrap mb-2">
              {photoPreviews.map((url, idx) => (
                <div key={idx} className="relative">
                  <img src={url} alt={`Foto ${idx + 1}`} className="h-24 w-24 object-cover rounded shadow" />
                  <button
                    type="button"
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full px-2 py-1 text-xs"
                    onClick={() => handleRemovePhoto(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {formData.photos.length < 1 && (
                <label className={`flex flex-col items-center justify-center h-24 w-24 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-blue-400 ${!formData.vehiclePlate ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <span className="text-xs text-gray-500">Agregar foto</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple={false}
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                    disabled={uploading || !formData.vehiclePlate}
                    ref={fileInputRef}
                  />
                </label>
              )}
            </div>
            <span className="text-xs text-gray-400">Puedes tomar una foto o elegir desde tu dispositivo.</span>
            {!formData.vehiclePlate && (
              <div className="text-xs text-red-500 mt-2">Seleccione un vehículo para habilitar la carga de fotos.</div>
            )}
            {uploading && <div className="text-blue-600 text-xs mt-2">Subiendo imagen...</div>}
          </div>
        </div>
        {errorMsg && (
          <div className="mb-2 text-red-600">{errorMsg}</div>
        )}
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={uploading}
        >
          {editingTrip ? "Guardar Cambios" : "Guardar Viaje"}
        </button>
        {editingTrip && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="ml-2 px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500"
          >
            Cancelar
          </button>
        )}
      </form>
    </div>
  );
};

export default TripForm;